import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Camera, Crown, UserMinus, UserPlus, LogOut } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PresenceAvatar from "@/components/PresenceAvatar";
import PeopleSearch from "@/components/PeopleSearch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GroupInfoDialog({ conv, me, open, onOpenChange, onChanged, onLeave }) {
  const isAdmin = conv?.admins?.includes(me);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => { if (open && conv) { setName(conv.name || ""); setDescription(conv.description || ""); setAdding(false); } }, [open, conv]);
  if (!conv) return null;

  const patch = async (body) => {
    try { const r = await axios.patch(`${API}/chat/groups/${conv.conversation_id}`, body); onChanged(r.data); toast.success("Grupo atualizado"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao atualizar"); }
  };
  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file, file.name);
      const r = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await patch({ picture: r.data.url });
    } catch { toast.error("Falha no upload"); }
    setUploading(false);
  };
  const removeMember = async (uid) => {
    if (!window.confirm("Remover este membro do grupo?")) return;
    try { await axios.delete(`${API}/chat/groups/${conv.conversation_id}/members/${uid}`); const r = await axios.get(`${API}/chat/conversations/${conv.conversation_id}`); onChanged(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const addMember = async (u) => {
    try { await axios.post(`${API}/chat/groups/${conv.conversation_id}/members`, { user_ids: [u.user_id] }); const r = await axios.get(`${API}/chat/conversations/${conv.conversation_id}`); onChanged(r.data); toast.success(`${u.name} adicionado(a)`); setAdding(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="group-info-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="w-4 h-4" /> Dados do grupo</DialogTitle></DialogHeader>
        <div className="flex flex-col items-center gap-2">
          <label className={`relative ${isAdmin ? "cursor-pointer" : ""}`}>
            <div className="w-24 h-24 rounded-full bg-primary/15 text-primary flex items-center justify-center overflow-hidden ring-4 ring-background shadow" data-testid="group-photo">
              {conv.picture ? <img src={conv.picture} alt="" className="w-full h-full object-cover" /> : <Users className="w-10 h-10" />}
            </div>
            {isAdmin && (<>
              <span className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow"><Camera className="w-4 h-4" /></span>
              <input type="file" accept="image/*" className="hidden" data-testid="group-photo-input" disabled={uploading} onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} />
            </>)}
          </label>
          {isAdmin ? (
            <div className="w-full space-y-2 mt-2">
              <Input data-testid="group-info-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Nome do grupo" />
              <Textarea data-testid="group-info-description" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} rows={2} placeholder="Descrição do grupo (opcional)" />
              <Button size="sm" className="rounded-full w-full" data-testid="group-info-save" disabled={!name.trim() || (name === conv.name && description === (conv.description || ""))} onClick={() => patch({ name: name.trim(), description })}>Salvar alterações</Button>
            </div>
          ) : (
            <div className="text-center mt-1">
              <div className="font-bold text-lg" data-testid="group-info-title">{conv.name}</div>
              {conv.description && <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="group-info-desc">{conv.description}</p>}
            </div>
          )}
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{conv.members?.length} membros</div>
            {isAdmin && <Button size="sm" variant="ghost" className="gap-1 text-xs" data-testid="group-add-member-toggle" onClick={() => setAdding(a => !a)}><UserPlus className="w-3.5 h-3.5" /> Adicionar</Button>}
          </div>
          {adding && <div className="mb-3"><PeopleSearch autoFocus showActions={false} placeholder="Buscar pessoa para adicionar..." onSelect={addMember} /></div>}
          <div className="space-y-1" data-testid="group-members-list">
            {(conv.members_info || []).map(u => (
              <div key={u.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted" data-testid={`group-member-${u.user_id}`}>
                <PresenceAvatar src={u.picture} name={u.name} online={u.online} className="w-9 h-9" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate flex items-center gap-1">{u.name}{u.user_id === me && <span className="text-[10px] text-muted-foreground">(você)</span>}{conv.admins?.includes(u.user_id) && <Crown className="w-3.5 h-3.5 text-amber-500" title="Admin" />}</div>
                  <div className="text-[11px] text-muted-foreground">{u.online ? "online" : "offline"}</div>
                </div>
                {isAdmin && u.user_id !== me && <Button size="icon" variant="ghost" className="text-red-500" data-testid={`group-member-remove-${u.user_id}`} onClick={() => removeMember(u.user_id)} aria-label="Remover"><UserMinus className="w-4 h-4" /></Button>}
              </div>
            ))}
          </div>
        </div>
        <Button variant="outline" className="rounded-full w-full gap-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="group-info-leave" onClick={onLeave}><LogOut className="w-4 h-4" /> Sair do grupo</Button>
      </DialogContent>
    </Dialog>
  );
}
